import React, { useEffect, useId, useRef, useState } from 'react';
import * as Clipboard from 'expo-clipboard';
import Icon from '@/components/Icon';
import Logo from '@/components/Icons/Logo';
import { LEARN_DEMOS } from '@/content/learnDemos';

const STEPS = ['Write the prompt', 'Read the answer', 'Ask for a change', 'Check the result'];
const STEP_MS = 8000;

function DemoPlayer({ demo, active }) {
  const id = useId();
  const root = useRef(null);
  const [step, setStep] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(true);
  const [status, setStatus] = useState('');
  const [typed, setTyped] = useState(0);
  const composer = step === 0 ? demo.prompt : step === 2 ? demo.followUp : '';
  const response = step === 3 ? demo.revised : demo.response;
  const animatedText = composer || response;

  useEffect(() => {
    const media = window.matchMedia?.('(prefers-reduced-motion: reduce)');
    const update = () => { setReducedMotion(media?.matches ?? true); if (media?.matches) setPlaying(false); };
    update();
    media?.addEventListener?.('change', update);
    const pauseWhenHidden = () => { if (document.hidden) setPlaying(false); };
    document.addEventListener('visibilitychange', pauseWhenHidden);
    const observer = typeof IntersectionObserver === 'undefined' ? null : new IntersectionObserver(entries => {
      if (!entries[0].isIntersecting) setPlaying(false);
    });
    if (root.current) observer?.observe(root.current);
    return () => {
      media?.removeEventListener?.('change', update);
      document.removeEventListener('visibilitychange', pauseWhenHidden);
      observer?.disconnect();
    };
  }, []);
  useEffect(() => { if (!active) setPlaying(false); }, [active]);
  useEffect(() => {
    if (!playing || !active || reducedMotion) return undefined;
    const timer = setTimeout(() => {
      if (step === STEPS.length - 1) setPlaying(false);
      else { setStep(value => value + 1); setTyped(0); }
    }, STEP_MS);
    return () => clearTimeout(timer);
  }, [playing, active, reducedMotion, step]);
  useEffect(() => {
    if (!playing || !active || reducedMotion || typed >= animatedText.length) return undefined;
    const timer = setTimeout(() => setTyped(value => Math.min(animatedText.length, value + 8)), 55);
    return () => clearTimeout(timer);
  }, [playing, active, reducedMotion, animatedText, typed]);

  function go(next) { setPlaying(false); setStep(next); setTyped(0); setStatus(''); }
  async function copy() {
    try {
      const copied = await Clipboard.setStringAsync(demo.source ? `${demo.prompt}\n\nMeeting notes:\n${demo.source}` : demo.prompt);
      setStatus(copied === false ? 'Could not copy. Select the example prompt to copy it.' : 'Example prompt copied. Paste it into Gab AI to try it.');
    } catch { setStatus('Could not copy. Select the example prompt to copy it.'); }
  }
  const playbackControl = reducedMotion
    ? <button className="learn-demo-play" onClick={() => go((step + 1) % STEPS.length)}>{step === 3 ? 'Start again' : 'Next step'} <Icon name="arrow-right" size={12} color="var(--learn-ink)" /></button>
    : <button className="learn-demo-play" onClick={() => { if (step === 3 && !playing) { setStep(0); setTyped(0); } setPlaying(value => !value); }}><Icon name={playing ? 'pause' : 'play'} size={12} color="var(--learn-ink)" />{playing ? 'Pause' : step === 3 ? 'Replay example' : 'Play example'}</button>;
  return <div ref={root} className="learn-demo-player" data-playing={playing && !reducedMotion}>
    <div className="learn-demo-window" role="group" aria-label="Example Gab AI conversation">
      <div className="learn-demo-bar"><span><Logo width={24} height={24} color="currentColor" /> Gab AI <span className="learn-fine">Chat</span></span>{playbackControl}</div>
      <div className="learn-demo-stage" key={step}>
        <div className="learn-demo-conversation">
          {step === 0 ? <div className="learn-demo-empty"><Logo width={34} height={34} color="currentColor" /><h3>What can I help with?</h3><p>Start with something you want to get done.</p></div> : <>
            <div className="learn-demo-user"><span className="learn-demo-sr">You: </span>{step === 3 ? demo.followUp : demo.prompt}</div>
            <div className="learn-demo-answer"><span className="learn-demo-speaker"><Logo width={18} height={18} color="currentColor" /> Gab AI</span><p><span className="learn-demo-sr">{response}</span><span aria-hidden="true">{playing && !reducedMotion && !composer ? response.slice(0, typed) : response}<span className="learn-demo-untyped">{playing && !reducedMotion && !composer ? response.slice(typed) : ''}</span></span></p></div>
          </>}
        </div>
        <div className={`learn-demo-composer ${composer ? 'is-active' : ''}`}>
          {demo.attachment && step === 0 && <details className="learn-demo-file"><summary><Icon name="file-lines" size={15} color="var(--learn-ink)" />{demo.attachment}<span>View sample</span></summary><p>{demo.source}</p></details>}
          <div className="learn-demo-input">
            {composer ? <><span className="learn-demo-sr">Example prompt: {composer}</span><span aria-hidden="true">{playing && !reducedMotion ? composer.slice(0, typed) : composer}<span className="learn-demo-untyped">{playing && !reducedMotion ? composer.slice(typed) : ''}</span></span></> : <span className="learn-demo-placeholder">Ask a follow-up…</span>}
          </div>
          <div className="learn-demo-toolbar"><span aria-hidden="true"><Icon name="plus" size={16} color="var(--learn-muted)" /></span><span className="learn-demo-model" aria-hidden="true">Auto <Icon name="chevron-down" size={9} color="var(--learn-muted)" /></span><button className="learn-demo-send" disabled={!composer} aria-label="Show the example response" onClick={() => go(step + 1)}><Icon name="arrow-up" size={16} color="var(--learn-bg)" /></button></div>
        </div>
      </div>
    </div>
    <div className="learn-demo-step-picker" role="group" aria-label="Walkthrough steps">{STEPS.map((label, index) => <button key={label} aria-pressed={step === index} aria-controls={`${id}-caption`} onClick={() => go(index)}><span>{index + 1}</span>{label}</button>)}</div>
    <div id={`${id}-caption`} className="learn-demo-caption" role="status"><p>{demo.captions[step]}</p></div>
    <div className="learn-demo-controls">
      <p className="learn-fine">Example walkthrough · Prepared responses</p>
      <button className="learn-text-button" onClick={copy}>Copy example prompt</button>
    </div>
    {status && <p className="learn-fine learn-demo-copy" role="status">{status}</p>}
  </div>;
}

export default function LearnDemo({ demoId = 'invitation', chooser = false, active = true }) {
  const [selected, setSelected] = useState(demoId);
  const selectedId = chooser ? selected : demoId;
  const demo = LEARN_DEMOS[selectedId];
  const id = useId();
  if (!demo) return null;
  return <section id={chooser ? 'learn-examples' : undefined} className="learn-demo" aria-labelledby={`${id}-title`}>
    <div className="learn-section-heading"><div><p className="learn-eyebrow">See it in Gab AI</p><h2 id={`${id}-title`}>{chooser ? 'Watch a useful conversation unfold.' : demo.title}</h2></div></div>
    {chooser && <div className="learn-demo-chooser" role="group" aria-label="Choose an example">{Object.entries(LEARN_DEMOS).map(([key, item]) => <button key={key} aria-pressed={key === selectedId} onClick={() => setSelected(key)}>{item.label}</button>)}</div>}
    <DemoPlayer key={selectedId} demo={demo} active={active} />
  </section>;
}
